// ** Modules
import React from 'react';

// ** UI
import Box from '@mui/material/Box';
import Pagination from '@mui/material/Pagination';
import CircularProgress from '@mui/material/CircularProgress';
import { useTheme } from '@mui/material/styles';

// ** Components
import Section from '@/components/common/Section';
import EventCard from './EventCard';
import CardsGrid from '@/components/common/CardsGrid';
import ComponentBlocksSearchFilter from '@/components/strapi/Shared/ComponentBlocksSearchFilter';

// ** Utils
import { useGetEvents, useGetEventPeriods, useGetEventCategories } from '@/lib/utils/eventDataUtils';

// ** Hooks
import { useSearchLogic } from '@/hooks/useSearchLogic';

// ** Types
import type { Event } from '@/types/graphql';
import type { EventCardModel } from './EventCard';
import type { StrapiComponentProps } from '@/types/strapi';
import type { ComponentBlocksEvents as BlocksEventsProps } from '@/types/graphql';

type ComponentBlocksEventsProps = StrapiComponentProps<BlocksEventsProps>;
type EventMapProps = {
	event: Event;
	basePath: string;
};

const mapEventToCardModel = ({ event, basePath }: EventMapProps): EventCardModel => {
	const isOnline =
		(event.eventType?.trim().toLowerCase().includes('online') ||
			event.eventType?.trim().toLowerCase().includes('webinar')) ??
		false;

	const maxParticipants = event.maxParticipantsNumber ?? 0;
	const actualParticipants = event.actualNumberOfParticipants ?? 0;

	const isSoldOut = maxParticipants > 0 && actualParticipants >= maxParticipants;
	const availableParticipants = maxParticipants - actualParticipants;

	const isFewPlacesLeft = !isSoldOut && maxParticipants > 0 && availableParticipants / maxParticipants <= 0.2;

	// Determine default availability label dynamically
	let availabilityLabel: string | undefined;

	if (event.status === 'storniert') {
		availabilityLabel = 'Abgesagt';
	} else if (isSoldOut) {
		availabilityLabel = 'Ausgebucht';
	} else if (isFewPlacesLeft) {
		availabilityLabel = 'wenige Plätze';
	}

	const cleanBasePath = basePath.replace(/\/$/, '');
	const dateLabel = event.start ? event.start : !event.startDate ? `${event.startDate}` : '';

	return {
		id: event.id,
		dateLabel: dateLabel,
		mode: isOnline ? 'online' : 'praesenz',
		subline: event.eventName || event.title || 'Unbekannte Veranstaltung',
		copy: event.description || '',
		href: `${cleanBasePath}/${event.eventNumber || event.id}`,
		ctaLabel: 'Jetzt anmelden',
		isSoldOut,
		isFewPlacesLeft,
		availabilityLabel,
		field: event.field || ''
	};
};

const ComponentBlocksEvents = ({
	slug,
	containerSx,
	eventsPerPage,
	eventsFilterTools,
	eventsSearchFilters
}: ComponentBlocksEventsProps) => {
	const theme = useTheme();
	const sp = theme.spacing;
	const sectionRef = React.useRef<HTMLDivElement>(null);
	const { searchState, searchHandlers } = useSearchLogic();
	const { handlePageChange } = searchHandlers;

	const pageSize = eventsPerPage || 8;

	// Fetch Data via GraphQL (which calls the SQL Resolver)
	const { events, loading } = useGetEvents({
		pagination: {
			page: searchState.page,
			pageSize
		},
		filters: {
			eventTitle: { containsi: searchState.searchTerm },
			eventCategory: { eq: searchState.selectedCategory },
			startDate: { year: searchState.selectedPeriod }
		}
	});

	// Metadata for UI
	const { categories } = useGetEventCategories({
		allLabel: eventsSearchFilters?.searchFilterCategoryPlaceholder
	});
	const { periods } = useGetEventPeriods({
		allLabel: eventsSearchFilters?.searchFilterPeriodPlaceholder
	});

	const eventsCount = events?.totalCount || 0;
	const eventsArray = events?.nodes || [];
	const totalPages = Math.max(1, Math.ceil(eventsCount / pageSize));

	// Snap back to page 1 when filters shrink the result set below the current page.
	React.useEffect(() => {
		if (!loading && searchState.page > totalPages) {
			handlePageChange(1);
		}
	}, [loading, searchState.page, totalPages, handlePageChange]);

	const handlePaginationChange = (_event: React.ChangeEvent<unknown>, value: number) => {
		handlePageChange(value);

		if (sectionRef.current) {
			window.dispatchEvent(new CustomEvent('programmatic-scroll', { detail: true }));

			const navbarEl = document.querySelector<HTMLElement>('[class*="MuiAppBar"]');
			const navbarHeight = navbarEl?.offsetHeight ?? 100;
			const elementTop = sectionRef.current.getBoundingClientRect().top + window.scrollY;

			window.scrollTo({
				top: Math.max(0, elementTop - navbarHeight - 16),
				behavior: 'smooth'
			});

			setTimeout(() => {
				window.dispatchEvent(new CustomEvent('programmatic-scroll', { detail: false }));
			}, 1000);
		}
	};

	const searchFilterConfig = eventsSearchFilters
		? ({
				...eventsSearchFilters,
				searchFilterShowCategory: eventsSearchFilters.searchFilterShowCategory ?? undefined,
				searchFilterShowPeriod: eventsSearchFilters.searchFilterShowPeriod ?? undefined,
				searchFilterShowSearch: eventsSearchFilters.searchFilterShowSearch ?? undefined,
				searchFilterCategoryPlaceholder: eventsSearchFilters.searchFilterCategoryPlaceholder ?? undefined,
				searchFilterPeriodPlaceholder: eventsSearchFilters.searchFilterPeriodPlaceholder ?? undefined,
				searchFilterSearchPlaceholder: eventsSearchFilters.searchFilterSearchPlaceholder ?? undefined
			} as React.ComponentProps<typeof ComponentBlocksSearchFilter>['config'])
		: undefined;

	return (
		<Section backgroundColor="heroLightBackground.main" containerSx={containerSx}>
			<div ref={sectionRef} />
			{eventsFilterTools && (
				<Box sx={{ mb: { xs: sp(8), md: sp(12) } }}>
					<ComponentBlocksSearchFilter
						config={searchFilterConfig}
						options={{ categories, periods }}
						state={searchState}
						handlers={searchHandlers}
						backgroundColor={theme.palette.heroLightBackground?.main as string}
					/>
				</Box>
			)}

			<CardsGrid
				showAvailabilityCount={eventsFilterTools && !loading && eventsCount > 0}
				availabilityLabelSuffix={`von ${eventsCount} Veranstaltungen gefunden`}
				totalCount={eventsCount}
				showEmptyState={!loading}
			>
				{eventsArray.map((event) => (
					<EventCard key={event.id} event={mapEventToCardModel({ event, basePath: slug || '' })} />
				))}
			</CardsGrid>

			{loading && (
				<Box sx={{ py: 10, textAlign: 'center', width: '100%' }}>
					<CircularProgress color="primary" size={40} />
				</Box>
			)}

			{!loading && totalPages > 1 && (
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'center',
						mt: { xs: sp(8), md: sp(12) }
					}}
				>
					<Pagination
						page={searchState.page}
						count={totalPages}
						shape="rounded"
						siblingCount={1}
						onChange={handlePaginationChange}
						aria-label="Veranstaltungs-Seitennavigation"
					/>
				</Box>
			)}
		</Section>
	);
};

export default ComponentBlocksEvents;
